import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { AdminPageHeader, Field, TextArea, Toggle, Select, ConfirmDeleteButton } from "./_helpers";
import MediaPicker from "@/components/admin/MediaPicker";
import { toast } from "sonner";
import { X, ArrowUp, ArrowDown, GripVertical, Save, Star } from "lucide-react";

function slugify(s) { return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }

export default function AdminColumnists() {
  const [items, setItems] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [columns, setColumns] = useState([]);
  const [activeTab, setActiveTab] = useState("writers");
  const empty = { name: "", slug: "", photo: "", bio: "", province_id: "", social: {}, is_active: true, sort_order: 0, user_id: "" };
  const emptyCol = { columnist_id: "", title: "", slug: "", content: "", cover_image: "", is_active: true };
  const [f, setF] = useState(empty);
  const [cf, setCf] = useState(emptyCol);
  const [editing, setEditing] = useState(null);
  const [editingCol, setEditingCol] = useState(null);
  const [users, setUsers] = useState([]);

  const load = () => api.get("/columnists?admin=true").then((r) => setItems(r.data));
  const loadCols = () => api.get("/columns?admin=true&limit=200").then((r) => setColumns(r.data));
  useEffect(() => {
    load();
    loadCols();
    api.get("/provinces?active_only=false").then((r) => setProvinces(r.data));
    api.get("/users?limit=500").then((r) => setUsers(r.data.items || r.data || [])).catch(() => setUsers([]));
  }, []);

  // Drag-and-drop reorder for the columns table. We keep the in-flight
  // order in local state, only POST /columns/reorder when the admin
  // actually drops something, and show a small "Sırayı Kaydet" button
  // so one slip of the mouse doesn't invisibly save a bad order.
  const dragFromRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  // Reset the "dirty" flag whenever the server list is reloaded.
  useEffect(() => { setOrderDirty(false); }, [columns.length]);

  const moveColumn = (from, to) => {
    if (from === to || from == null || to == null) return;
    setColumns((cur) => {
      const next = cur.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setOrderDirty(true);
  };
  const onColDragStart = (idx) => (e) => {
    dragFromRef.current = idx;
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* some browsers */ }
  };
  const onColDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const onColDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragFromRef.current;
    dragFromRef.current = null;
    setDragOverIdx(null);
    moveColumn(from, idx);
  };
  const onColDragEnd = () => { dragFromRef.current = null; setDragOverIdx(null); };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.post("/columns/reorder", { ids: columns.map((c) => c.id) });
      toast.success("Sıra kaydedildi");
      setOrderDirty(false);
      loadCols();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sıra kaydedilemedi");
    } finally {
      setSavingOrder(false);
    }
  };

  // Toggle the "home page lead" flag. Clicking a non-featured row stars
  // the row (and clears the feature flag from its sibling columns, so
  // every columnist has at most one featured piece). Clicking again on
  // the same row un-features it — the home block then falls back to the
  // most recent column for that columnist.
  const toggleFeature = async (col) => {
    try {
      const r = await api.post(`/columns/${col.id}/feature`);
      // Mirror the server state locally so the table updates instantly.
      setColumns((cur) =>
        cur.map((c) =>
          c.columnist_id === col.columnist_id
            ? { ...c, is_featured: c.id === col.id ? r.data.featured : false }
            : c,
        ),
      );
      toast.success(
        r.data.featured
          ? `“${col.title}” artık ${col.columnist?.name || "yazarın"} ana sayfadaki yazısı`
          : "Vurgu kaldırıldı — ana sayfada en yeni yazı gösterilecek",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    }
  };

  const move = async (id, dir) => {
    try {
      await api.post(`/columnists/${id}/reorder`, { direction: dir });
      load();
    } catch { toast.error("Sıralama güncellenemedi"); }
  };

  const saveW = async () => {
    try { const payload = { ...f, province_id: f.province_id || null, slug: f.slug || slugify(f.name) };
      if (editing) await api.put(`/columnists/${editing}`, payload); else await api.post("/columnists", payload);
      setF(empty); setEditing(null); load(); toast.success("Kaydedildi");
    } catch { toast.error("Hata"); }
  };
  const saveCol = async () => {
    if (!cf.columnist_id) return toast.error("Yazar seçiniz");
    if (!cf.title?.trim()) return toast.error("Başlık zorunlu");
    if (!cf.content?.trim()) return toast.error("İçerik zorunlu");
    try {
      const payload = { ...cf, slug: cf.slug || slugify(cf.title) };
      if (editingCol) await api.put(`/columns/${editingCol}`, payload);
      else await api.post("/columns", payload);
      setCf(emptyCol); setEditingCol(null); loadCols();
      toast.success(editingCol ? "Yazı güncellendi" : "Yazı eklendi");
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };
  const editCol = (c) => {
    setEditingCol(c.id);
    setCf({
      columnist_id: c.columnist_id || c.columnist?.id || "",
      title: c.title || "",
      slug: c.slug || "",
      content: c.content || "",
      cover_image: c.cover_image || "",
      is_active: c.is_active !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEditCol = () => { setEditingCol(null); setCf(emptyCol); };

  return (
    <div>
      <AdminPageHeader title="Köşe Yazarları & Yazılar" />
      <div className="flex gap-2 mb-4">
        <button onClick={() => setActiveTab("writers")} className={`px-3 py-1.5 rounded text-sm ${activeTab === "writers" ? "bg-[#e11d2a] text-white" : "border border-gray-200"}`}>Yazarlar</button>
        <button onClick={() => setActiveTab("columns")} className={`px-3 py-1.5 rounded text-sm ${activeTab === "columns" ? "bg-[#e11d2a] text-white" : "border border-gray-200"}`}>Yazılar</button>
      </div>
      {activeTab === "writers" ? (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-100 rounded-lg max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0"><tr><th className="px-3 py-2 text-left">Sıra</th><th className="px-3 py-2 text-left">Ad</th><th className="px-3 py-2 text-left">İl</th><th></th></tr></thead>
              <tbody>
                {items.map((c, i) => (
                  <tr key={c.id} className="border-t">
                    <td className="px-2 py-2 whitespace-nowrap">
                      <div className="flex flex-col gap-0.5">
                        <button
                          type="button"
                          onClick={() => move(c.id, "up")}
                          disabled={i === 0}
                          data-testid={`columnist-up-${c.id}`}
                          className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                          title="Yukarı taşı"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(c.id, "down")}
                          disabled={i === items.length - 1}
                          data-testid={`columnist-down-${c.id}`}
                          className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                          title="Aşağı taşı"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 flex items-center gap-2"><img src={c.photo} alt="" className="w-8 h-8 rounded-full object-cover bg-gray-100" />{c.name}</td>
                    <td className="px-3 py-2 text-xs">{provinces.find((p) => p.id === c.province_id)?.name || "Genel"}</td>
                    <td className="px-3 py-2 text-right space-x-2"><button onClick={() => { setEditing(c.id); setF({ ...c, province_id: c.province_id || "", user_id: c.user_id || "" }); }} className="text-blue-600 text-xs">Düzenle</button><ConfirmDeleteButton onConfirm={async () => { await api.delete(`/columnists/${c.id}`); load(); }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white border border-gray-100 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between"><h3 className="font-bold">{editing ? "Düzenle" : "Yeni Yazar"}</h3>{editing && <button onClick={() => { setEditing(null); setF(empty); }} className="text-gray-500"><X className="w-4 h-4" /></button>}</div>
            <Field label="Ad" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: f.slug || slugify(e.target.value) })} />
            <div>
              <label className="block text-xs font-medium mb-1 text-gray-600">Profil Fotoğrafı</label>
              <MediaPicker
                value={f.photo}
                onChange={(u) => setF({ ...f, photo: u })}
                scope="columnists"
                testId="columnist-photo-picker"
              />
            </div>
            <TextArea rows={2} label="Biyografi" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} />
            <Select label="İl" value={f.province_id || ""} options={[{ value: "", label: "Genel" }, ...provinces.map((p) => ({ value: p.id, label: p.name }))]} onChange={(e) => setF({ ...f, province_id: e.target.value })} />
            <Select
              label="Bağlı Kullanıcı (opsiyonel)"
              value={f.user_id || ""}
              options={[
                { value: "", label: "— (Sadece admin düzenleyebilir)" },
                ...users.map((u) => {
                  const nm = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username || u.email;
                  return { value: u.id, label: `${nm} (${u.email})` };
                }),
              ]}
              onChange={(e) => setF({ ...f, user_id: e.target.value })}
              data-testid="columnist-user-select"
            />
            <p className="text-[11px] text-gray-500 -mt-1">
              Bağladığınız kullanıcı bu yazarın profilini ve yazılarını kendi hesabından düzenleyebilir.
            </p>
            <Toggle label="Aktif" value={f.is_active} onChange={(v) => setF({ ...f, is_active: v })} />
            <button onClick={saveW} className="w-full px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold">Kaydet</button>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-100 rounded-lg max-h-[500px] overflow-y-auto" data-testid="admin-columns-list">
            {/* Drag-sort toolbar — only appears after the admin reorders
             *   something so the "clean" state stays visually quiet. */}
            {orderDirty && (
              <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs">
                <span className="text-amber-800">Sıralama değişti — henüz kaydedilmedi.</span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={loadCols}
                    className="px-2 py-1 border rounded text-xs hover:bg-white"
                    data-testid="column-order-reset"
                  >
                    Vazgeç
                  </button>
                  <button
                    onClick={saveOrder}
                    disabled={savingOrder}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#e11d2a] text-white rounded text-xs font-semibold hover:bg-[#c01825] disabled:opacity-60"
                    data-testid="column-order-save"
                  >
                    <Save className="w-3 h-3" /> {savingOrder ? "..." : "Sırayı Kaydet"}
                  </button>
                </div>
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0"><tr><th className="w-6 px-1"></th><th className="w-10 px-1 text-center" title="Ana sayfada ilk sırada görünecek yazı">★</th><th className="px-3 py-2 text-left">Başlık</th><th className="px-3 py-2 text-left">Yazar</th><th className="px-3 py-2 text-center">Aktif</th><th></th></tr></thead>
              <tbody onDragEnd={onColDragEnd}>
                {columns.map((c, idx) => (
                  <tr
                    key={c.id}
                    draggable
                    onDragStart={onColDragStart(idx)}
                    onDragOver={onColDragOver(idx)}
                    onDrop={onColDrop(idx)}
                    className={`border-t transition-colors ${
                      editingCol === c.id ? "bg-amber-50" : ""
                    } ${
                      dragOverIdx === idx ? "bg-blue-50 outline outline-2 outline-blue-300" : ""
                    } cursor-move`}
                    data-testid={`column-row-${c.id}`}
                  >
                    <td className="px-1 text-gray-300 text-center" aria-label="Sürükle">
                      <GripVertical className="w-4 h-4 inline" />
                    </td>
                    <td className="px-1 text-center">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleFeature(c); }}
                        className={`p-1 rounded transition ${
                          c.is_featured
                            ? "text-amber-500 hover:text-amber-600"
                            : "text-gray-300 hover:text-amber-400"
                        }`}
                        aria-label={c.is_featured ? "Vurguyu kaldır" : "Ana sayfada 1. sırada göster"}
                        title={
                          c.is_featured
                            ? `Bu yazı ${c.columnist?.name || "yazarın"} ana sayfadaki öne çıkan yazısı`
                            : "Ana sayfada 1. sırada göster"
                        }
                        data-testid={`column-feature-${c.id}`}
                      >
                        <Star
                          className="w-4 h-4"
                          fill={c.is_featured ? "currentColor" : "none"}
                        />
                      </button>
                    </td>
                    <td className="px-3 py-2 max-w-[240px]">
                      <div className="font-medium truncate flex items-center gap-1.5">
                        {c.title}
                        {c.is_featured && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 rounded px-1 py-0.5">
                            <Star className="w-2.5 h-2.5" fill="currentColor" /> ANA SAYFA
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">{c.columnist?.name}</td>
                    <td className="px-3 py-2 text-center text-xs">{c.is_active === false ? "—" : "✅"}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                      <button onClick={() => editCol(c)} className="text-blue-600 text-xs hover:underline" data-testid={`column-edit-${c.id}`}>Düzenle</button>
                      <ConfirmDeleteButton onConfirm={async () => { await api.delete(`/columns/${c.id}`); loadCols(); if (editingCol === c.id) cancelEditCol(); }} />
                    </td>
                  </tr>
                ))}
                {columns.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-gray-500">Yazı yok</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="bg-white border border-gray-100 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-bold">{editingCol ? "Yazıyı Düzenle" : "Yeni Yazı"}</h3>
              {editingCol && <button onClick={cancelEditCol} className="text-gray-500 hover:text-gray-700" data-testid="column-edit-cancel"><X className="w-4 h-4" /></button>}
            </div>
            <Select label="Yazar" value={cf.columnist_id} options={[{ value: "", label: "-" }, ...items.map((it) => ({ value: it.id, label: it.name }))]} onChange={(e) => setCf({ ...cf, columnist_id: e.target.value })} />
            <Field label="Başlık" value={cf.title} onChange={(e) => setCf({ ...cf, title: e.target.value, slug: cf.slug || slugify(e.target.value) })} />
            <Field label="Slug (URL)" value={cf.slug} onChange={(e) => setCf({ ...cf, slug: slugify(e.target.value) })} />
            <div>
              <label className="block text-xs font-medium mb-1 text-gray-600">Kapak Görseli</label>
              <MediaPicker
                value={cf.cover_image}
                onChange={(u) => setCf({ ...cf, cover_image: u })}
                scope="columns"
                testId="column-cover-picker"
              />
            </div>
            <TextArea rows={10} label="İçerik" value={cf.content} onChange={(e) => setCf({ ...cf, content: e.target.value })} />
            <Toggle label="Aktif" value={cf.is_active !== false} onChange={(v) => setCf({ ...cf, is_active: v })} />
            <button onClick={saveCol} className="w-full px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold" data-testid="column-save">
              {editingCol ? "Güncelle" : "Yayınla"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
